
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import type { SitemapGenerationLog } from '@/types/sitemap';

interface SitemapGenerationLogsProps {
  logs: SitemapGenerationLog[];
  isLoading: boolean;
}

export const SitemapGenerationLogs: React.FC<SitemapGenerationLogsProps> = ({
  logs,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex items-center space-x-4">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-20" />
          </div>
        ))}
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-gray-900 dark:text-gray-100">Date</TableHead>
            <TableHead className="text-gray-900 dark:text-gray-100">Type</TableHead>
            <TableHead className="text-gray-900 dark:text-gray-100">Status</TableHead>
            <TableHead className="text-gray-900 dark:text-gray-100">Entries</TableHead>
            <TableHead className="text-gray-900 dark:text-gray-100">File Size</TableHead>
            <TableHead className="text-gray-900 dark:text-gray-100">Generation Time</TableHead>
            <TableHead className="text-gray-900 dark:text-gray-100">Error</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center py-8 text-gray-600 dark:text-gray-400">
                No generation logs found
              </TableCell>
            </TableRow>
          ) : (
            logs.map((log) => (
              <TableRow key={log.id}>
                <TableCell className="text-sm text-gray-900 dark:text-gray-100">
                  {new Date(log.created_at).toLocaleString()}
                </TableCell>
                <TableCell>
                  <Badge variant="outline">
                    {log.generation_type}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge className={getStatusColor(log.status)}>
                    {log.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-gray-900 dark:text-gray-100">{log.entries_count.toLocaleString()}</TableCell>
                <TableCell className="text-gray-900 dark:text-gray-100">{formatFileSize(log.file_size_bytes)}</TableCell>
                <TableCell className="text-gray-900 dark:text-gray-100">
                  {log.generation_time_ms > 0 ? `${log.generation_time_ms}ms` : '-'}
                </TableCell>
                <TableCell className="text-sm text-red-600 dark:text-red-400 max-w-xs truncate">
                  {log.error_message || '-'}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
